// Mirrors the backend enumerations in app/models/enumerations/ambassador_*.py.

export enum RegistrationStatus {
  DRAFT = 0,
  SUBMITTED = 1,
  UNDER_REVIEW = 2,
  QUESTIONS_PENDING = 3,
  CONFIRMED = 4,
  REJECTED = 5,
  WITHDRAWN = 6,
}

export enum SalesStatus {
  OPEN = 0,
  QUALIFICATION = 1,
  OFFER_IN_PREPARATION = 2,
  OFFER_SUBMITTED = 3,
  NEGOTIATION = 4,
  WON = 5,
  LOST = 6,
}

export enum DealEventType {
  SUBMITTED = 0,
  REVIEW_STARTED = 1,
  QUESTION_ASKED = 2,
  QUESTION_ANSWERED = 3,
  CONFIRMED = 4,
  REJECTED = 5,
  WITHDRAWN = 6,
  REOPENED = 7,
  SALES_STATUS_CHANGED = 8,
  OWNER_ASSIGNED = 9,
  DATA_CORRECTED = 10,
  DOCUMENT_ADDED = 11,
  DOCUMENT_REMOVED = 12,
}

export enum CustomerRelationship {
  NEW_CUSTOMER = 0,
  EXISTING_CUSTOMER = 1,
  UNDECIDED = 2,
}

export enum OpportunityType {
  LEAD = 0,
  PROJECT_INQUIRY = 1,
  EXPANSION = 2,
  ORDER = 3,
}

export enum ServiceArea {
  AI_CAPABILITY_HUB = 0,
  CONSULTING_MANAGEMENT = 1,
  IT_SECURITY = 2,
  AUTOMATION = 3,
  SOFTWARE_DEVELOPMENT = 4,
  MANAGED_SERVICES = 5,
}

export enum DealReadiness {
  IDEA = 0,
  FIRST_MEETING = 1,
  NEED_CONFIRMED = 2,
  BUDGET_PLANNED = 3,
  DECISION_PENDING = 4,
}

export enum Contribution {
  RECOMMENDATION = 0,
  CONTACT_ESTABLISHED = 1,
  MEETING_SUPPORT = 2,
  CO_SELLING = 3,
}

export enum ContactAgreed {
  YES = 0,
  NO = 1,
  OPEN = 2,
}

export enum NewsCategory {
  PRODUCTS = 0,
  SALES = 1,
  EVENTS = 2,
  PLATFORM = 3,
}

export enum NewsStatus {
  DRAFT = 0,
  PUBLISHED = 1,
}

export const CURRENCIES = ["EUR", "CHF", "USD", "GBP"] as const;
export type Currency = (typeof CURRENCIES)[number];

/** The deal record the ambassador fills in over the four form steps. */
export interface DealData {
  company_name: string;
  customer_relationship: CustomerRelationship | null;
  company_website: string;
  company_country: string;
  contact_name: string;
  contact_role: string;
  contact_email: string;
  contact_agreed: ContactAgreed | null;
  title: string;
  opportunity_type: OpportunityType | null;
  description: string;
  service_areas: ServiceArea[];
  deal_readiness: DealReadiness | null;
  /** Quarter, e.g. "2027-Q1". */
  expected_start: string;
  expected_volume: number | null;
  currency: Currency;
  contribution: Contribution | null;
  next_step: string;
  additional_info: string;
  data_protection_confirmed: boolean;
}

export type DealField = keyof DealData;

export interface Paginated {
  total: number;
  page: number;
  page_size: number;
}

// Ambassador view

export interface DealListItem {
  id: number;
  deal_number: string | null;
  company_name: string;
  title: string;
  registration_status: RegistrationStatus;
  /** What the ambassador sees, only once como released it. */
  sales_status: SalesStatus | null;
  submitted_at: string | null;
  last_update_at: string;
}

export interface DealListResponse extends Paginated {
  deals: DealListItem[];
}

export interface DealHistoryEntry {
  id: number;
  event_type: DealEventType;
  note: string;
  created_at: string;
  sales_status?: SalesStatus | null;
}

export interface DealMessage {
  id: number;
  message: string;
  from_ambassador: boolean;
  is_question: boolean;
  author_name: string | null;
  created_at: string;
}

export interface DealDocument {
  id: number;
  filename: string;
  content_type: string;
  size: number;
  /** Opens in the image or PDF viewer; everything else is downloaded. */
  previewable: boolean;
  from_ambassador: boolean;
  uploaded_by_name: string | null;
  created_at: string;
}

/** The como view also says who uploaded a document. */
export interface InternalDealDocument extends DealDocument {
  uploaded_by: DealUser | null;
}

export interface Deal extends DealListItem, DealData {
  rejection_reason: string;
  owner_name: string | null;
  created_at: string;
  history: DealHistoryEntry[];
  messages: DealMessage[];
  documents: DealDocument[];
}

export interface DealSummary {
  total: number;
  drafts: number;
  in_review: number;
  questions_pending: number;
  confirmed: number;
  in_sales: number;
}

export interface DealListFilters {
  page: number;
  page_size?: number;
  search?: string;
  registration_status?: RegistrationStatus | null;
  sales_status?: SalesStatus | null;
}

// como view

export interface DealUser {
  id: number;
  name: string;
  surname: string;
  email: string;
}

export interface InternalDealListItem {
  id: number;
  deal_number: string;
  company_name: string;
  title: string;
  ambassador: DealUser | null;
  owner: DealUser | null;
  registration_status: RegistrationStatus;
  sales_status: SalesStatus | null;
  has_possible_duplicates: boolean;
  submitted_at: string;
  updated_at: string;
}

export interface InternalDealListResponse extends Paginated {
  deals: InternalDealListItem[];
}

export interface InternalDealEvent {
  id: number;
  event_type: DealEventType;
  old_value: unknown;
  new_value: unknown;
  note: string;
  visible_to_ambassador: boolean;
  actor: DealUser | null;
  created_at: string;
}

export interface DealNote {
  id: number;
  note: string;
  author: DealUser | null;
  created_at: string;
}

export interface DealDuplicate {
  id: number;
  deal_number: string;
  company_name: string;
  title: string;
  ambassador: DealUser | null;
  registration_status: RegistrationStatus;
  submitted_at: string;
}

export interface InternalDeal extends InternalDealListItem, DealData {
  workspace: { id: number; name: string } | null;
  released_sales_status: SalesStatus | null;
  external_sales_status: SalesStatus | null;
  rejection_reason: string;
  decided_at: string | null;
  external_system: string | null;
  external_id: string | null;
  created_at: string;
  possible_duplicates: DealDuplicate[];
  history: InternalDealEvent[];
  messages: DealMessage[];
  documents: InternalDealDocument[];
  notes: DealNote[];
}

export interface InternalDealSummary {
  submitted: number;
  under_review: number;
  questions_pending: number;
  closed: number;
  assigned_to_me: number;
}

export type OwnerFilter = "me" | "none" | number | null;

export interface InternalDealListFilters {
  page: number;
  page_size?: number;
  search?: string;
  registration_status?: RegistrationStatus | null;
  open_only?: boolean;
  owner?: OwnerFilter;
}

/** One review step as saved from the review panel; missing fields stay. */
export interface ProcessingPayload {
  owner_id?: number | null;
  registration_status?: RegistrationStatus;
  rejection_reason?: string;
  sales_status?: SalesStatus;
  release_sales_status?: boolean;
  internal_note?: string;
  external_message?: string;
}

// News

export interface NewsItem {
  id: number;
  title: string;
  teaser: string;
  /** Markdown. */
  content: string;
  link: string;
  category: NewsCategory;
  published_at: string | null;
}

export interface NewsListResponse extends Paginated {
  news: NewsItem[];
}

export interface EditorNewsItem extends NewsItem {
  status: NewsStatus;
  audience: string[];
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface EditorNewsListResponse extends Paginated {
  news: EditorNewsItem[];
}

export interface NewsPayload {
  title: string;
  teaser: string;
  content: string;
  link: string;
  category: NewsCategory;
  status: NewsStatus;
  published_at: string | null;
}
